import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowDown, ArrowUp, ArrowLeft, X, Zap, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useI18n } from '../i18n';

export function OnboardingGuide() {
  const { t } = useI18n();
  const STORAGE_KEY = 'flowfocus_onboarding_done';
  const STEPS = [
    {
      target: '[data-testid="quick-capture-input"]',
      title: t('onb1Title'),
      description: t('onb1Desc'),
      position: 'below',
      highlight: true,
    },
    {
      target: '[data-testid="kanban-board"]',
      title: t('onb2Title'),
      description: t('onb2Desc'),
      position: 'above',
      highlight: true,
    },
    {
      target: '[data-testid="nav-starred"]',
      title: t('onb3Title'),
      description: t('onb3Desc'),
      position: 'right',
      highlight: true,
    },
  ];
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const tooltipRef = useRef(null);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const positionTooltip = useCallback(() => {
    if (!visible) return;
    const current = STEPS[step];
    const el = document.querySelector(current.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();

    if (current.highlight) {
      el.classList.add('onboarding-highlight');
    }

    const pos = current.position;
    const gap = 16;
    let style = {};
    let aStyle = {};

    if (pos === 'below') {
      style = {
        position: 'fixed',
        top: rect.bottom + gap,
        left: Math.max(16, rect.left + rect.width / 2 - 160),
        zIndex: 9999,
      };
      aStyle = {
        position: 'fixed',
        top: rect.bottom + 2,
        left: rect.left + rect.width / 2 - 8,
        zIndex: 9999,
      };
    } else if (pos === 'above') {
      style = {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + gap,
        left: Math.max(16, rect.left + rect.width / 2 - 160),
        zIndex: 9999,
      };
      aStyle = {
        position: 'fixed',
        top: rect.top - gap - 4,
        left: rect.left + rect.width / 2 - 8,
        zIndex: 9999,
      };
    } else if (pos === 'right') {
      style = {
        position: 'fixed',
        top: rect.top - 10,
        left: rect.right + gap,
        zIndex: 9999,
      };
      aStyle = {
        position: 'fixed',
        top: rect.top + rect.height / 2 - 8,
        left: rect.right + 2,
        zIndex: 9999,
      };
    }

    setTooltipStyle(style);
    setArrowStyle(aStyle);
  }, [step, visible]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [positionTooltip]);

  useEffect(() => {
    // Clean previous highlights
    document.querySelectorAll('.onboarding-highlight').forEach(el => {
      el.classList.remove('onboarding-highlight');
    });
    if (visible) {
      positionTooltip();
    }
  }, [step, visible, positionTooltip]);

  const handleNext = () => {
    document.querySelectorAll('.onboarding-highlight').forEach(el => {
      el.classList.remove('onboarding-highlight');
    });
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    document.querySelectorAll('.onboarding-highlight').forEach(el => {
      el.classList.remove('onboarding-highlight');
    });
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!visible) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const ArrowIcon = current.position === 'below' ? ArrowUp : 
                    current.position === 'above' ? ArrowDown : ArrowLeft;

  return (
    <>
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="w-80 bg-[#1a1a1e] border border-yellow-500/20 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
        data-testid="onboarding-tooltip"
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between px-4 pt-3 pb-0">
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-xs text-yellow-400 font-medium">
              {t('step')} {step + 1} {t('onboardingOf')} {STEPS.length}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
            data-testid="onboarding-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pt-2 pb-3">
          <h3 className="text-sm font-semibold text-zinc-100 mb-1 font-['Outfit']">
            {current.title}
          </h3>
          <p className="text-xs text-zinc-400 leading-relaxed">
            {current.description}
          </p>
        </div>

        {/* Step dots + button */}
        <div className="flex items-center justify-between px-4 pb-3">
          <div className="flex gap-1.5">
            {STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === step ? 'w-4 bg-yellow-400' : 'w-1.5 bg-zinc-700'
                }`}
              />
            ))}
          </div>
          <Button
            onClick={handleNext}
            size="sm"
            className="h-7 px-3 bg-yellow-400 text-black hover:bg-yellow-300 text-xs font-medium"
            data-testid="onboarding-next-btn"
          >
            {isLast ? t('done_btn') : t('next')}
            {!isLast && <ChevronRight className="w-3 h-3 ml-1" />}
          </Button>
        </div>
      </div>
    </>
  );
}
